import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { live } from 'lit/directives/live.js';

import '@spectrum-web-components/button/sp-button.js';
import '@spectrum-web-components/switch/sp-switch.js';
import '@spectrum-web-components/dialog/sp-dialog.js';
import '@spectrum-web-components/field-label/sp-field-label.js';
import '@spectrum-web-components/menu/sp-menu.js';
import '@spectrum-web-components/menu/sp-menu-item.js';
import '@spectrum-web-components/picker/sp-picker.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/textfield/sp-textfield.js';

import '../../components/confirmation-dialog';
import { api } from '../../api/index.js';
import type {
  KlardatenCredentialsDto,
  KlardatenInstance,
  KlardatenProfile,
} from '../../api/klardaten-credentials.js';
import type { McpServerConfig, McpToolInfo } from '../../api/mcp-servers.js';
import { environment } from '../../config/environment.js';
import {
  getKlardatenInstanceLabel,
  getKlardatenProfilePrimaryLabel,
  getKlardatenProfileSecondaryLabel,
} from '../../utils/klardaten-display';
import { getNextProfileDefault } from './klardaten-defaults';
import {
  buildKlardatenMcpHints,
  getKlardatenMcpServerName,
} from './mcp-settings-utils';
import { settingsSharedStyles } from './settings-shared.styles';

type McpOAuthPopupMessage = {
  type: 'klarvos:mcp-oauth';
  status: 'success' | 'error';
  message: string;
};

type KlardatenWizardStep = 'instance' | 'profile' | 'confirm';

@customElement('kv-settings-mcp')
@localized()
export class KvSettingsMcp extends LitElement {
  static styles = [
    settingsSharedStyles,
    css`
      .mcp-panel-grid {
        align-items: stretch;
        display: grid;
        gap: 12px;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      }

      .field-spaced {
        margin-top: 12px;
      }

    .server-list {
        display: flex;
        flex-direction: column;
        gap: 0;
    }

    .server-row {
      display: flex;
      align-items: center;
        gap: 14px;
        padding: 14px 0;
        border-bottom: 1px solid var(--settings-border);
    }

    .server-row:last-child {
      border-bottom: none;
    }

    .server-info {
      flex: 1;
      min-width: 0;
    }

    .server-name {
      font-weight: 600;
      color: var(--color-app-text);
        line-height: 1.25;
    }

    .server-url {
      font-size: 13px;
      color: var(--color-app-text-secondary);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .server-status {
      margin-top: 6px;
    }

    .server-actions {
      display: flex;
      gap: 8px;
      align-items: center;
      flex-shrink: 0;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .tools-list {
        margin: 0 0 12px;
        padding: 10px 12px;
        border: 1px solid var(--settings-border);
        border-radius: 8px;
        background: var(--settings-surface-muted);
      font-size: 13px;
    }

    .tools-list summary {
      cursor: pointer;
      font-weight: 500;
      color: var(--color-app-text-secondary);
      margin-bottom: 4px;
    }

    .tool-item {
      padding: 4px 0;
      color: var(--color-app-text);
    }

    .tool-name {
      font-family: monospace;
      font-size: 12px;
    }

    .tool-desc {
      color: var(--color-app-text-secondary);
      font-size: 12px;
    }

    .wizard-modal-host {
      position: fixed;
      inset: 0;
      display: grid;
      place-items: center;
      z-index: 6;
    }

    .wizard-modal-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1;
      background: var(--color-modal-overlay, rgba(15, 18, 26, 0.55));
    }

    .wizard-modal-container {
      position: relative;
      z-index: 2;
      width: min(620px, calc(100vw - 32px));
    }

    .wizard-modal-container sp-dialog {
      background: var(--color-sidebar-bg, #fff);
      width: 100%;
    }

    .wizard-title {
      color: var(--color-app-text);
      font-size: 18px;
      font-weight: 700;
      line-height: 1.25;
      margin: 0;
    }

    .wizard-description,
    .wizard-summary-value {
      color: var(--color-app-text-secondary);
      font-size: 13px;
      line-height: 1.4;
      margin: 0;
    }

    .wizard-content {
      display: grid;
      gap: 14px;
      min-width: min(500px, calc(100vw - 64px));
    }

    .wizard-steps {
      display: grid;
      gap: 0;
      grid-template-columns: repeat(var(--wizard-step-count), minmax(0, 1fr));
      margin: 2px 0 22px;
    }

    .wizard-step {
      align-items: center;
      color: var(--color-app-text-secondary);
      display: flex;
      font-size: 12px;
      font-weight: 650;
      gap: 8px;
      min-width: 0;
      position: relative;
    }

    .wizard-step::after {
      background: var(--settings-border);
      content: '';
      height: 2px;
      left: 32px;
      position: absolute;
      right: 10px;
      top: 13px;
      z-index: 0;
    }

    .wizard-step:last-child::after {
      display: none;
    }

    .wizard-step-index {
      align-items: center;
      background: var(--color-sidebar-bg, #fff);
      border: 1px solid var(--settings-border);
      border-radius: 999px;
      color: var(--color-app-text-secondary);
      display: inline-flex;
      flex: 0 0 auto;
      height: 28px;
      justify-content: center;
      position: relative;
      width: 28px;
      z-index: 1;
    }

    .wizard-step-label {
      background: var(--color-sidebar-bg, #fff);
      overflow: hidden;
      padding-inline-end: 8px;
      position: relative;
      text-overflow: ellipsis;
      white-space: nowrap;
      z-index: 1;
    }

    .wizard-step.active {
      color: var(--color-app-text);
    }

    .wizard-step.active .wizard-step-index {
      background: color-mix(
        in srgb,
        var(--color-sidebar-selected-border, #3f5efb) 10%,
        white
      );
      border-color: var(--color-sidebar-selected-border, #3f5efb);
      color: var(--color-sidebar-selected-border, #3f5efb);
      box-shadow: 0 0 0 3px
        color-mix(
          in srgb,
          var(--color-sidebar-selected-border, #3f5efb) 12%,
          transparent
        );
    }

    .wizard-step.complete {
      color: var(--color-app-text);
    }

    .wizard-step.complete::after,
    .wizard-step.complete .wizard-step-index {
      background: var(--color-sidebar-selected-border, #3f5efb);
      border-color: var(--color-sidebar-selected-border, #3f5efb);
      color: white;
    }

    .wizard-loading {
      align-items: center;
      display: flex;
      gap: 8px;
      min-height: 44px;
    }

    .wizard-step-title {
      color: var(--color-app-text);
      font-size: 14px;
      font-weight: 700;
      line-height: 1.3;
      margin: 0;
    }

    .wizard-callout {
      background: var(--settings-surface-muted);
      border: 1px solid var(--settings-border);
      border-radius: 8px;
      display: grid;
      gap: 6px;
      padding: 14px;
    }

    .wizard-callout-title {
      color: var(--color-app-text);
      font-size: 13px;
      font-weight: 700;
      line-height: 1.35;
      margin: 0;
    }

    .wizard-summary {
      border: 1px solid var(--settings-border);
      border-radius: 8px;
      display: grid;
      gap: 10px;
      padding: 12px;
    }

    .wizard-summary-row {
      display: grid;
      gap: 2px;
    }

    .wizard-summary-label {
      color: var(--color-app-text);
      font-size: 12px;
      font-weight: 600;
    }

    .wizard-summary-value {
      margin: 0;
    }

    .profile-row {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .profile-secondary {
      color: var(--color-app-text-secondary);
      font-size: 11px;
    }

    .wizard-content sp-picker {
      width: 100%;
    }

    .wizard-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      margin-top: 20px;
    }

      @media (max-width: 760px) {
        .server-row {
          align-items: stretch;
          flex-direction: column;
        }

        .server-actions {
          justify-content: flex-start;
        }
      }
    `,
  ];

  @state()
  accessor servers: McpServerConfig[] = [];

  @state()
  accessor tools: McpToolInfo[] = [];

  @state()
  accessor nameInput = '';

  @state()
  accessor urlInput = '';

  @state()
  accessor loading = false;

  @state()
  accessor adding = false;

  @state()
  accessor addingKlardaten = false;

  @state()
  accessor showKlardatenWizard = false;

  @state()
  accessor klardatenWizardStep: KlardatenWizardStep = 'instance';

  @state()
  accessor klardatenCredentials: KlardatenCredentialsDto | null = null;

  @state()
  accessor klardatenInstances: KlardatenInstance[] = [];

  @state()
  accessor klardatenProfiles: KlardatenProfile[] = [];

  @state()
  accessor selectedKlardatenInstanceId = '';

  @state()
  accessor selectedKlardatenProfileId = '';

  @state()
  accessor loadingKlardatenOptions = false;

  @state()
  accessor loadingKlardatenProfiles = false;

  @state()
  accessor klardatenWizardError: string | null = null;

  @state()
  accessor message: { type: 'success' | 'error'; text: string } | null = null;

  @state()
  accessor pendingServerDelete: McpServerConfig | null = null;

  private readonly onOAuthMessage = (event: MessageEvent<unknown>) => {
    const allowedOrigins = new Set([window.location.origin]);
    try {
      allowedOrigins.add(new URL(environment.apiBaseUrl).origin);
    } catch {
      // ignore invalid api base urls
    }

    if (!allowedOrigins.has(event.origin)) {
      return;
    }

    const data = event.data as Partial<McpOAuthPopupMessage> | null;
    if (!data || data.type !== 'klarvos:mcp-oauth') {
      return;
    }

    this.message = {
      type: data.status === 'success' ? 'success' : 'error',
      text: data.message ?? msg('MCP authorization finished.'),
    };
    void this.loadData();
  };

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void this.loadData();
    }
  };

  override connectedCallback() {
    super.connectedCallback();
    window.addEventListener('message', this.onOAuthMessage);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    void this.loadData();
  }

  override disconnectedCallback() {
    window.removeEventListener('message', this.onOAuthMessage);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    super.disconnectedCallback();
  }

  private async loadData() {
    this.loading = true;
    try {
      const [servers, tools] = await Promise.all([
        api.mcpServers.listServers(),
        api.mcpServers.listTools().catch(() => []),
      ]);
      this.servers = servers ?? [];
      this.tools = tools ?? [];
    } catch {
      this.servers = [];
      this.tools = [];
    } finally {
      this.loading = false;
    }
  }

  private openOAuthPopup(
    serverId: number,
    popup?: Window | null,
    hints?: { instanceId?: string | null; profileId?: string | null }
  ) {
    const target =
      popup ?? window.open('', 'klarvos-mcp-oauth', 'width=640,height=820');
    if (!target) {
      this.message = {
        type: 'error',
        text: msg('Popup blocked. Allow popups and try Connect again.'),
      };
      return;
    }

    target.location.href = api.mcpServers.getOAuthStartUrl(serverId, hints);
  }

  private async addServer() {
    this.adding = true;
    this.message = null;
    const popup = window.open('', 'klarvos-mcp-oauth', 'width=640,height=820');

    try {
      const server = await api.mcpServers.addServer({
        name: this.nameInput.trim(),
        url: this.urlInput.trim(),
      });
      this.nameInput = '';
      this.urlInput = '';
      this.message = {
        type: 'success',
        text: msg('Server added. Complete the OAuth flow in the popup.'),
      };
      await this.loadData();
      this.openOAuthPopup(server.id, popup);
    } catch (err: unknown) {
      popup?.close();
      const text =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : msg('Failed to add server.');
      this.message = { type: 'error', text };
    } finally {
      this.adding = false;
    }
  }

  private errorText(err: unknown, fallback: string): string {
    return err && typeof err === 'object' && 'message' in err
      ? String((err as { message: string }).message)
      : fallback;
  }

  private async openKlardatenWizard() {
    this.showKlardatenWizard = true;
    this.klardatenWizardStep = 'instance';
    this.klardatenCredentials = null;
    this.klardatenInstances = [];
    this.klardatenProfiles = [];
    this.selectedKlardatenInstanceId = '';
    this.selectedKlardatenProfileId = '';
    this.klardatenWizardError = null;
    this.message = null;
    await this.loadKlardatenWizardOptions();
  }

  private openKlardatenSettings() {
    this.cancelKlardatenWizard();
    this.dispatchEvent(
      new CustomEvent('settings-tab-requested', {
        detail: { tab: 'klardaten' },
        bubbles: true,
        composed: true,
      })
    );
  }

  private cancelKlardatenWizard() {
    this.showKlardatenWizard = false;
    this.klardatenWizardError = null;
  }

  private async loadKlardatenWizardOptions() {
    this.loadingKlardatenOptions = true;
    try {
      const credentials = await api.klardatenCredentials
        .get()
        .catch(() => null);
      this.klardatenCredentials = credentials;
      if (!credentials?.hasKey) return;

      const instances = await api.klardatenCredentials.listInstances();
      this.klardatenInstances = Array.isArray(instances) ? instances : [];
      const defaultInstanceId = credentials.defaultClientInstanceId ?? '';
      const hasDefaultInstance = this.klardatenInstances.some(
        (instance) => instance.id === defaultInstanceId
      );
      if (hasDefaultInstance) {
        this.selectedKlardatenInstanceId = defaultInstanceId;
        await this.loadKlardatenProfiles(defaultInstanceId);
      }
    } catch (err: unknown) {
      this.klardatenWizardError = this.errorText(
        err,
        msg('Failed to load Klardaten options.')
      );
      this.klardatenInstances = [];
      this.klardatenProfiles = [];
    } finally {
      this.loadingKlardatenOptions = false;
    }
  }

  private async loadKlardatenProfiles(instanceId: string) {
    this.loadingKlardatenProfiles = true;
    this.klardatenProfiles = [];
    this.selectedKlardatenProfileId = '';
    try {
      const profiles = await api.klardatenCredentials.listProfiles(instanceId);
      this.klardatenProfiles = Array.isArray(profiles) ? profiles : [];
      const currentDefault =
        instanceId === this.klardatenCredentials?.defaultClientInstanceId
          ? this.klardatenCredentials.defaultProfileId
          : null;
      const currentIsAvailable = this.klardatenProfiles.some(
        (profile) => profile.profile_id === currentDefault
      );
      const preferredProfileId = currentIsAvailable ? currentDefault : null;
      this.selectedKlardatenProfileId =
        getNextProfileDefault(this.klardatenProfiles, preferredProfileId) ??
        preferredProfileId ??
        '';
    } catch (err: unknown) {
      this.klardatenWizardError = this.errorText(
        err,
        msg('Failed to load Klardaten profiles.')
      );
      this.klardatenProfiles = [];
      this.selectedKlardatenProfileId = '';
    } finally {
      this.loadingKlardatenProfiles = false;
    }
  }

  private async onKlardatenWizardInstanceChange(e: Event) {
    const value = String((e.target as { value?: unknown })?.value ?? '');
    this.selectedKlardatenInstanceId = value;
    this.selectedKlardatenProfileId = '';
    this.klardatenProfiles = [];
    this.klardatenWizardError = null;
    if (value) {
      await this.loadKlardatenProfiles(value);
    }
  }

  private onKlardatenWizardProfileChange(e: Event) {
    this.selectedKlardatenProfileId = String(
      (e.target as { value?: unknown })?.value ?? ''
    );
  }

  private getSelectedKlardatenInstance(): KlardatenInstance | null {
    return (
      this.klardatenInstances.find(
        (instance) => instance.id === this.selectedKlardatenInstanceId
      ) ?? null
    );
  }

  private getSelectedKlardatenProfile(): KlardatenProfile | null {
    return (
      this.klardatenProfiles.find(
        (profile) => profile.profile_id === this.selectedKlardatenProfileId
      ) ?? null
    );
  }

  private getKlardatenWizardServerName(): string {
    return getKlardatenMcpServerName(
      this.servers,
      this.getSelectedKlardatenInstance()
    );
  }

  private continueKlardatenWizard() {
    if (this.klardatenWizardStep === 'instance') {
      this.klardatenWizardStep = this.selectedKlardatenInstanceId
        ? 'profile'
        : 'confirm';
      return;
    }
    if (this.klardatenWizardStep === 'profile') {
      this.klardatenWizardStep = 'confirm';
    }
  }

  private backKlardatenWizard() {
    if (this.klardatenWizardStep === 'confirm') {
      this.klardatenWizardStep = this.selectedKlardatenInstanceId
        ? 'profile'
        : 'instance';
      return;
    }
    if (this.klardatenWizardStep === 'profile') {
      this.klardatenWizardStep = 'instance';
    }
  }

  private async connectKlardatenWizard() {
    this.addingKlardaten = true;
    this.message = null;
    this.klardatenWizardError = null;
    const popup = window.open('', 'klarvos-mcp-oauth', 'width=640,height=820');

    try {
      const server = await api.mcpServers.addKlardatenServer({
        name: this.getKlardatenWizardServerName(),
      });
      await this.loadData();
      this.showKlardatenWizard = false;
      this.openOAuthPopup(
        server.id,
        popup,
        buildKlardatenMcpHints(
          this.selectedKlardatenInstanceId,
          this.selectedKlardatenProfileId
        )
      );
    } catch (err: unknown) {
      popup?.close();
      this.klardatenWizardError = this.errorText(
        err,
        msg('Failed to add Klardaten MCP server.')
      );
    } finally {
      this.addingKlardaten = false;
    }
  }

  private async toggleServer(server: McpServerConfig) {
    if (server.connectionStatus !== 'connected') {
      this.message = {
        type: 'error',
        text: msg('Connect the server first before enabling it for chat.'),
      };
      return;
    }

    try {
      await api.mcpServers.updateServer(server.id, {
        enabled: !server.enabled,
      });
      await this.loadData();
    } catch (err: unknown) {
      const text =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : msg('Failed to update server.');
      this.message = { type: 'error', text };
    }
  }

  private async testExistingServer(server: McpServerConfig) {
    try {
      const result = await api.mcpServers.testSavedServer(server.id);
      if (result.success) {
        this.message = {
          type: 'success',
          text: msg(
            str`Connected to ${result.serverInfo?.name ?? 'server'}. Found ${result.toolCount ?? 0} tool(s).`
          ),
        };
      } else {
        this.message = {
          type: 'error',
          text:
            result.error ??
            (result.needsAuthorization
              ? msg('OAuth authorization is required.')
              : msg('Connection failed.')),
        };
      }
    } catch (err: unknown) {
      const text =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : msg('Connection test failed.');
      this.message = { type: 'error', text };
    }
  }

  private requestDeleteServer(server: McpServerConfig) {
    this.pendingServerDelete = server;
  }

  private cancelDeleteServer = () => {
    this.pendingServerDelete = null;
  };

  private confirmDeleteServer = async () => {
    const server = this.pendingServerDelete;
    if (!server) return;
    this.pendingServerDelete = null;
    try {
      await api.mcpServers.deleteServer(server.id);
      this.message = { type: 'success', text: msg('Server removed.') };
      await this.loadData();
    } catch (err: unknown) {
      const text =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: string }).message)
          : msg('Failed to delete server.');
      this.message = { type: 'error', text };
    }
  };

  private getToolsForServer(serverId: number): McpToolInfo[] {
    return this.tools.filter((t) => t.serverConfigId === serverId);
  }

  private getStatusLabel(server: McpServerConfig): string {
    switch (server.connectionStatus) {
      case 'connected':
        return msg('Connected');
      case 'authorizing':
        return msg('Authorizing');
      default:
        return msg('Not connected');
    }
  }

  private getStatusClass(server: McpServerConfig): string {
    switch (server.connectionStatus) {
      case 'connected':
        return 'settings-chip success server-status';
      case 'authorizing':
        return 'settings-chip warning server-status';
      default:
        return 'settings-chip neutral server-status';
    }
  }

  private getConnectButtonLabel(server: McpServerConfig): string {
    switch (server.connectionStatus) {
      case 'connected':
        return msg('Reconnect');
      case 'authorizing':
        return msg('Continue OAuth');
      default:
        return msg('Connect');
    }
  }

  private getWizardStepItems(): Array<{
    key: KlardatenWizardStep;
    label: string;
  }> {
    if (!this.klardatenCredentials?.hasKey) {
      return [
        { key: 'instance', label: msg('Setup') },
        { key: 'confirm', label: msg('Connect') },
      ];
    }

    return [
      { key: 'instance', label: msg('Instance') },
      ...(this.selectedKlardatenInstanceId
        ? [{ key: 'profile' as const, label: msg('Profile') }]
        : []),
      { key: 'confirm', label: msg('Connect') },
    ];
  }

  private renderKlardatenWizardSteps() {
    const steps = this.getWizardStepItems();
    const activeIndex = Math.max(
      0,
      steps.findIndex((step) => step.key === this.klardatenWizardStep)
    );
    return html`
      <div
        class="wizard-steps"
        style="--wizard-step-count: ${steps.length}"
        aria-label=${msg('Klardaten MCP connection steps')}
      >
        ${steps.map((step, index) => {
          const status =
            index < activeIndex
              ? 'complete'
              : index === activeIndex
                ? 'active'
                : '';
          return html`
            <span class="wizard-step ${status}">
              <span class="wizard-step-index">${index + 1}</span>
              <span class="wizard-step-label">${step.label}</span>
            </span>
          `;
        })}
      </div>
    `;
  }

  private renderKlardatenNoKeyStep() {
    return html`
      <div class="wizard-content">
        <div class="wizard-callout">
          <h4 class="wizard-callout-title">
            ${msg('Add a Klardaten API key to prefill this connection')}
          </h4>
          <p class="wizard-description">
            ${msg(
              'Save a Klardaten API key to choose an instance and profile before connecting.'
            )}
          </p>
        </div>
        <p class="wizard-description">
          ${msg(
            'You can still connect Klardaten MCP now and choose the instance during authentication.'
          )}
        </p>
      </div>
    `;
  }

  private renderKlardatenNoInstancesStep() {
    return html`
      <div class="wizard-content">
        <div class="wizard-callout">
          <h4 class="wizard-callout-title">
            ${msg('No Klardaten instances found')}
          </h4>
          <p class="wizard-description">
            ${msg(
              'Klarvos could not load any instances for the saved API key. You can still connect MCP and choose the instance during authentication.'
            )}
          </p>
        </div>
      </div>
    `;
  }

  private renderKlardatenWizardIntro() {
    if (this.klardatenWizardStep === 'profile') {
      return html`
        <h4 class="wizard-step-title">${msg('Choose a profile')}</h4>
        <p class="wizard-description">
          ${msg(
            'Profiles are optional. Use the default profile, pick an existing profile, or finish selection in MCP.'
          )}
        </p>
      `;
    }

    if (this.klardatenWizardStep === 'confirm') {
      return html`
        <h4 class="wizard-step-title">${msg('Review connection details')}</h4>
        <p class="wizard-description">
          ${msg(
            'These selections are used only to start the MCP connection. Klarvos does not send your saved API key to MCP.'
          )}
        </p>
      `;
    }

    return html`
      <h4 class="wizard-step-title">${msg('Choose an instance')}</h4>
      <p class="wizard-description">
        ${msg(
          'Start with a generic connection, or choose one of your existing Klardaten instances now.'
        )}
      </p>
    `;
  }

  private renderProfileOption(profile: KlardatenProfile) {
    return html`
      <sp-menu-item value=${profile.profile_id}>
        ${getKlardatenProfilePrimaryLabel(profile)}
        <span slot="description">
          ${getKlardatenProfileSecondaryLabel(profile)}
        </span>
      </sp-menu-item>
    `;
  }

  private renderKlardatenInstanceStep() {
    if (this.loadingKlardatenOptions) {
      return html`
        <div class="wizard-loading">
          <sp-progress-circle
            size="s"
            label=${msg('Loading Klardaten instances')}
            indeterminate
          ></sp-progress-circle>
          <span class="wizard-description">
            ${msg('Loading Klardaten instances...')}
          </span>
        </div>
      `;
    }

    if (!this.klardatenCredentials?.hasKey) {
      return this.renderKlardatenNoKeyStep();
    }

    if (this.klardatenInstances.length === 0) {
      return this.renderKlardatenNoInstancesStep();
    }

    return html`
      <div class="wizard-content">
        ${this.renderKlardatenWizardIntro()}
        <div class="settings-form-row">
          <sp-field-label for="klardaten-mcp-instance">
            ${msg('Instance')}
          </sp-field-label>
          <select
            class="settings-native-select"
            id="klardaten-mcp-instance"
            .value=${live(this.selectedKlardatenInstanceId)}
            @change=${(e: Event) =>
              void this.onKlardatenWizardInstanceChange(e)}
          >
            <option value="" ?selected=${!this.selectedKlardatenInstanceId}>
              ${msg('No instance')}
            </option>
            ${this.klardatenInstances.map((instance) => {
              const instanceId = instance.id ?? '';
              return html`
                <option
                  value=${instanceId}
                  ?selected=${instanceId === this.selectedKlardatenInstanceId}
                >
                  ${getKlardatenInstanceLabel(instance)}
                </option>
              `;
            })}
          </select>
        </div>
      </div>
    `;
  }

  private renderKlardatenProfileStep() {
    return html`
      <div class="wizard-content">
        ${this.renderKlardatenWizardIntro()}
        ${
          this.loadingKlardatenProfiles
            ? html`
                <div class="wizard-loading">
                  <sp-progress-circle
                    size="s"
                    label=${msg('Loading Klardaten profiles')}
                    indeterminate
                  ></sp-progress-circle>
                  <span class="wizard-description">
                    ${msg('Loading profiles...')}
                  </span>
                </div>
              `
            : ''
        }
        <div class="settings-form-row">
          <sp-field-label for="klardaten-mcp-profile">
            ${msg('Profile')}
          </sp-field-label>
          <sp-picker
            id="klardaten-mcp-profile"
            label=${msg('Select a profile')}
            .value=${live(this.selectedKlardatenProfileId)}
            ?disabled=${this.loadingKlardatenProfiles}
            @change=${(e: Event) => this.onKlardatenWizardProfileChange(e)}
          >
            <sp-menu-item value="">${msg('Default profile')}</sp-menu-item>
            ${this.klardatenProfiles.map((profile) =>
              this.renderProfileOption(profile)
            )}
          </sp-picker>
        </div>
      </div>
    `;
  }

  private renderKlardatenConfirmStep() {
    const instance = this.getSelectedKlardatenInstance();
    const profile = this.getSelectedKlardatenProfile();
    return html`
      <div class="wizard-content">
        ${this.renderKlardatenWizardIntro()}
        <div class="wizard-summary">
          <div class="wizard-summary-row">
            <span class="wizard-summary-label">${msg('Name')}</span>
            <span class="wizard-summary-value">
              ${this.getKlardatenWizardServerName()}
            </span>
          </div>
          <div class="wizard-summary-row">
            <span class="wizard-summary-label">${msg('Instance')}</span>
            <span class="wizard-summary-value">
              ${instance ? getKlardatenInstanceLabel(instance) : msg('No instance')}
            </span>
          </div>
          <div class="wizard-summary-row">
            <span class="wizard-summary-label">${msg('Profile')}</span>
            ${
              profile
                ? html`
                    <span class="profile-row">
                      <span>${getKlardatenProfilePrimaryLabel(profile)}</span>
                      <span class="profile-secondary">
                        ${getKlardatenProfileSecondaryLabel(profile)}
                      </span>
                    </span>
                  `
                : html`
                    <span class="wizard-summary-value">
                      ${msg('Default profile')}
                    </span>
                  `
            }
          </div>
        </div>
      </div>
    `;
  }

  private renderKlardatenWizardContent() {
    switch (this.klardatenWizardStep) {
      case 'profile':
        return this.renderKlardatenProfileStep();
      case 'confirm':
        return this.renderKlardatenConfirmStep();
      default:
        return this.renderKlardatenInstanceStep();
    }
  }

  private renderKlardatenWizardActions() {
    const isBusy =
      this.addingKlardaten ||
      this.loadingKlardatenOptions ||
      this.loadingKlardatenProfiles;
    const shouldConnectGenerically =
      this.klardatenWizardStep === 'instance' &&
      !this.loadingKlardatenOptions &&
      (!this.klardatenCredentials?.hasKey ||
        this.klardatenInstances.length === 0);
    return html`
      <div class="wizard-actions">
        <sp-button
          variant="secondary"
          ?disabled=${this.addingKlardaten}
          @click=${() => this.cancelKlardatenWizard()}
        >
          ${msg('Cancel')}
        </sp-button>
        ${
          this.klardatenWizardStep === 'instance' &&
          !this.klardatenCredentials?.hasKey
            ? html`
                <sp-button
                  variant="secondary"
                  ?disabled=${this.addingKlardaten}
                  @click=${() => this.openKlardatenSettings()}
                >
                  ${msg('Add API key')}
                </sp-button>
              `
            : ''
        }
        ${
          this.klardatenWizardStep === 'instance' || shouldConnectGenerically
            ? ''
            : html`
                <sp-button
                  variant="secondary"
                  ?disabled=${this.addingKlardaten}
                  @click=${() => this.backKlardatenWizard()}
                >
                  ${msg('Back')}
                </sp-button>
              `
        }
        ${
          this.klardatenWizardStep === 'confirm' || shouldConnectGenerically
            ? html`
                <sp-button
                  variant="accent"
                  ?disabled=${isBusy}
                  @click=${() => void this.connectKlardatenWizard()}
                >
                  ${
                    this.addingKlardaten
                      ? msg('Adding...')
                      : shouldConnectGenerically
                        ? msg('Connect generically')
                        : msg('Connect')
                  }
                </sp-button>
              `
            : html`
                <sp-button
                  variant="accent"
                  ?disabled=${isBusy}
                  @click=${() => this.continueKlardatenWizard()}
                >
                  ${msg('Next')}
                </sp-button>
              `
        }
      </div>
    `;
  }

  private renderKlardatenWizard() {
    if (!this.showKlardatenWizard) return '';

    return html`
      <div class="wizard-modal-host">
        <div
          class="wizard-modal-backdrop"
          @click=${() => this.cancelKlardatenWizard()}
        ></div>
        <div class="wizard-modal-container">
          <sp-dialog open size="m" role="dialog">
            <h2 slot="heading" class="wizard-title">
              ${msg('Connect Klardaten MCP')}
            </h2>
            ${this.renderKlardatenWizardSteps()}
            ${
              this.klardatenWizardError
                ? html`
                    <div class="settings-message error" role="status">
                      ${this.klardatenWizardError}
                    </div>
                  `
                : ''
            }
            ${this.renderKlardatenWizardContent()}
            ${this.renderKlardatenWizardActions()}
          </sp-dialog>
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      return html`
        <div class="settings-loading">
          <sp-progress-circle
            size="m"
            label=${msg('Loading MCP server settings')}
            indeterminate
          ></sp-progress-circle>
        </div>
      `;
    }

    return html`
      <section class="settings-section">
        <header class="settings-header">
          <h2 class="settings-title">${msg('MCP Servers')}</h2>
          <p class="settings-description">
            ${msg(
              'Configure remote MCP servers that expose a Streamable HTTP endpoint and support MCP OAuth.'
            )}
          </p>
        </header>

        <div class="mcp-panel-grid">
          <div class="settings-panel">
            <div class="settings-panel-header">
              <div>
                <h3 class="settings-panel-title">${msg('Connect to Klardaten')}</h3>
                <p class="settings-panel-description">
                  ${msg('Quickly add a Klardaten MCP server to connect your data sources.')}
                </p>
              </div>
            </div>
            <sp-button
              variant="accent"
              ?disabled=${this.addingKlardaten}
              @click=${() => void this.openKlardatenWizard()}
            >
              ${this.addingKlardaten ? msg('Adding...') : msg('Connect Klardaten MCP')}
            </sp-button>
          </div>

          <div class="settings-panel">
            <div class="settings-panel-header">
              <div>
                <h3 class="settings-panel-title">${msg('Add custom server')}</h3>
                <p class="settings-panel-description">
                  ${msg(
                    'Connect to any MCP-compatible server that supports Streamable HTTP and OAuth.'
                  )}
                </p>
              </div>
            </div>

            <div class="settings-form-row">
              <sp-field-label for="mcp-name">${msg('Server name')}</sp-field-label>
              <sp-textfield
                id="mcp-name"
                placeholder=${msg('e.g. Example MCP')}
                .value=${this.nameInput}
                @input=${(e: Event) => {
                  this.nameInput = (e.target as HTMLInputElement)?.value ?? '';
                }}
              ></sp-textfield>
            </div>

            <div class="settings-form-row field-spaced">
              <sp-field-label for="mcp-url">${msg('Server URL')}</sp-field-label>
              <sp-textfield
                id="mcp-url"
                placeholder=${msg(
                  'e.g. https://example-server.modelcontextprotocol.io/mcp'
                )}
                .value=${this.urlInput}
                @input=${(e: Event) => {
                  this.urlInput = (e.target as HTMLInputElement)?.value ?? '';
                }}
              ></sp-textfield>
            </div>

            <div class="settings-actions">
              <sp-button
                variant="accent"
                ?disabled=${this.adding || !this.nameInput.trim() || !this.urlInput.trim()}
                @click=${() => void this.addServer()}
              >
                ${this.adding ? msg('Adding...') : msg('Add and connect')}
              </sp-button>
            </div>
          </div>
        </div>

        ${
          this.message
            ? html`
              <div
                class="settings-message ${this.message.type}"
                role="status"
              >
                ${this.message.text}
              </div>
            `
            : ''
        }

        ${
          this.servers.length > 0
            ? html`
              <div class="settings-panel">
                <div class="settings-panel-header">
                  <div>
                    <h3 class="settings-panel-title">
                      ${msg(str`Configured servers (${this.servers.length})`)}
                    </h3>
                    <p class="settings-panel-description">
                      ${msg('Manage authorization, enabled state, and server diagnostics.')}
                    </p>
                  </div>
                </div>
                <div class="server-list">
                  ${this.servers.map(
                    (server) => html`
                      <div class="server-row">
                        <div class="server-info">
                          <div class="server-name">${server.name}</div>
                          <div class="server-url">${server.url}</div>
                          <div class=${this.getStatusClass(server)}>
                            ${this.getStatusLabel(server)}
                          </div>
                        </div>
                        <div class="server-actions">
                          <sp-switch
                            ?checked=${server.enabled}
                            ?disabled=${server.connectionStatus !== 'connected'}
                            @change=${() => void this.toggleServer(server)}
                          >
                            ${msg('Enabled')}
                          </sp-switch>
                          <sp-button
                            variant="accent"
                            size="s"
                            @click=${() => this.openOAuthPopup(server.id)}
                          >
                            ${this.getConnectButtonLabel(server)}
                          </sp-button>
                          <sp-button
                            variant="secondary"
                            size="s"
                            ?disabled=${server.connectionStatus !== 'connected'}
                            @click=${() => void this.testExistingServer(server)}
                          >
                            ${msg('Test')}
                          </sp-button>
                          <sp-button
                            variant="secondary"
                            size="s"
                            class="settings-danger-button"
                            @click=${() => this.requestDeleteServer(server)}
                          >
                            ${msg('Delete')}
                          </sp-button>
                        </div>
                      </div>
                      ${
                        this.getToolsForServer(server.id).length > 0
                          ? html`
                            <details class="tools-list">
                              <summary>
                                ${msg(
                                  str`${this.getToolsForServer(server.id).length} tool(s) discovered`
                                )}
                              </summary>
                              ${this.getToolsForServer(server.id).map(
                                (tool) => html`
                                  <div class="tool-item">
                                    <span class="tool-name"
                                      >${tool.originalToolName}</span
                                    >
                                    ${
                                      tool.description
                                        ? html`<span class="tool-desc">
                                          - ${tool.description}
                                        </span>`
                                        : ''
                                    }
                                  </div>
                                `
                              )}
                            </details>
                          `
                          : ''
                      }
                    `
                  )}
                </div>
              </div>
            `
            : ''
        }

        ${this.renderKlardatenWizard()}
        ${
          this.pendingServerDelete
            ? html`
              <kv-confirmation-dialog
                heading=${msg('Delete MCP server')}
                confirm-label=${msg('Delete')}
                @cancel=${this.cancelDeleteServer}
                @confirm=${this.confirmDeleteServer}
              >
                <p>
                  ${msg(
                    str`Delete "${this.pendingServerDelete.name}"? Stored OAuth tokens for this server will be deleted and you will need to connect again.`
                  )}
                </p>
              </kv-confirmation-dialog>
            `
            : ''
        }
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-settings-mcp': KvSettingsMcp;
  }
}
